import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, Col, Skeleton } from 'antd';
import notification from '../../../commonComponents/Notification/Notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import type { AppDispatch, RootState } from '../../../redux/store';
import { fetchAdminReports } from './store/adminReportsSlice';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import {
  METRIC_CARDS,
  METRIC_CARDS_CONFIG,
  EXPORT_TYPES,
  REFRESH_INTERVAL,
  generatePDF,
  type MetricCardType,
} from './constants';
import {
  Container,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  MainTitle,
  Subtitle,
  MetricsGrid,
  ActionButton,
  RefreshButton,
  ExportButton,
  StyledProgress,
  StyledAlert,
  SummaryCard,
  SummaryGrid,
  SummaryBox,
  SummaryValue,
  SummaryLabel,
  LastUpdatedText,
  DividerStyled,
} from './Styles';
import MetricCard from './MetricCard';
import MerchantsSection from './MerchantsSection';
import DevicesSection from './DevicesSection';
import SupportTicketsSection from './SupportTicketsSection';
import type { ExportData } from './types';

const AdminReports: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );

  const {
    merchants,
    devices,
    totalMerchants,
    activeDevices,
    supportTickets,
    totalRevenue,
    totalOrders,
    recentTransactions,
    performanceMetrics,
    loading,
    error,
    lastUpdated,
  } = useSelector((state: RootState) => state.adminReports);

  const [selectedMetric, setSelectedMetric] = useState<MetricCardType | null>(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    dispatch(fetchAdminReports());

    const refreshInterval = setInterval(() => {
      dispatch(fetchAdminReports());
    }, REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [dispatch]);

  const handleMetricClick = useCallback(
    (metricId: MetricCardType) => {
      const newSelectedMetric = selectedMetric === metricId ? null : metricId;
      setSelectedMetric(newSelectedMetric);
    },
    [selectedMetric]
  );

  const handleRefresh = useCallback(() => {
    dispatch(fetchAdminReports());
    notification.success('Reports Refreshed', 'All report data has been updated successfully.');
  }, [dispatch]);

  const handleExportReport = useCallback(
    async (type: string) => {
      try {
        setIsExporting(true);
        setExportProgress(0);

        const progressInterval = setInterval(() => {
          setExportProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 100);

        let exportData: ExportData = {
          reportType: type,
          generatedAt: new Date().toISOString(),
        };

        switch (type) {
          case EXPORT_TYPES.MERCHANTS_OVERVIEW:
            exportData = {
              reportType: EXPORT_TYPES.MERCHANTS_OVERVIEW,
              generatedAt: new Date().toISOString(),
              totalCount: totalMerchants,
              data: merchants.map((m) => ({
                name: m.name || m.business_name || 'N/A',
                address: m.address || 'N/A',
                email: m.email || 'N/A',
                phone: m.phone || 'N/A',
                status: m.status !== undefined ? m.status : true,
              })),
            };
            break;

          case EXPORT_TYPES.ACTIVE_DEVICES:
            exportData = {
              reportType: EXPORT_TYPES.ACTIVE_DEVICES,
              generatedAt: new Date().toISOString(),
              totalCount: activeDevices,
              data: devices.map((d) => ({
                name: d.name || 'N/A',
                identifier: d.identifier || 'N/A',
                type: d.type?.name || 'Unknown',
                status: d.status !== undefined ? d.status : true,
              })),
            };
            break;

          case EXPORT_TYPES.COMPREHENSIVE_REPORT:
            exportData = {
              reportType: EXPORT_TYPES.COMPREHENSIVE_REPORT,
              generatedAt: new Date().toISOString(),
              summary: {
                totalMerchants: totalMerchants || 0,
                activeDevices: activeDevices || 0,
                supportTickets: supportTickets || 0,
                totalRevenue: totalRevenue || 0,
                totalOrders: totalOrders || 0,
                performanceMetrics: performanceMetrics || {
                  conversionRate: 0,
                  averageOrderValue: 0,
                  customerSatisfaction: 0,
                  systemUptime: 0,
                },
              },
              detailedData: {
                merchants: merchants.map((m) => ({
                  name: m.name || m.business_name || 'N/A',
                  address: m.address || 'N/A',
                  email: m.email || 'N/A',
                  phone: m.phone || 'N/A',
                  status: m.status !== undefined ? m.status : true,
                })),
                devices: devices.map((d) => ({
                  name: d.name || 'N/A',
                  identifier: d.identifier || 'N/A',
                  type: d.type?.name || 'Unknown',
                  status: d.status !== undefined ? d.status : true,
                })) as any,
                transactions: recentTransactions || [],
                performanceMetrics: performanceMetrics || {
                  conversionRate: 0,
                  averageOrderValue: 0,
                  customerSatisfaction: 0,
                  systemUptime: 0,
                },
              },
            };
            break;

          default:
            exportData = { reportType: type, generatedAt: new Date().toISOString() };
        }

        const doc = generatePDF(type, exportData);
        const fileName = `${type.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        clearInterval(progressInterval);
        setExportProgress(100);

        notification.success('PDF Generated Successfully', `${type} has been generated and downloaded as ${fileName}`);
      } catch (error: any) {
        console.error('Export error:', error);
        notification.error('PDF Export Failed', 'Failed to export report. Please try again.');
      } finally {
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
        }, 1000);
      }
    },
    [
      merchants,
      devices,
      totalMerchants,
      activeDevices,
      supportTickets,
      totalRevenue,
      totalOrders,
      recentTransactions,
      performanceMetrics,
    ]
  );

  const getMetricValue = useCallback(
    (metricId: MetricCardType): string | number => {
      switch (metricId) {
        case METRIC_CARDS.TOTAL_MERCHANTS:
          return totalMerchants;
        case METRIC_CARDS.ACTIVE_DEVICES:
          return activeDevices;
        case METRIC_CARDS.SUPPORT_TICKETS:
          return supportTickets;
        case METRIC_CARDS.TOTAL_REVENUE:
          return `$${totalRevenue.toLocaleString()}`;
        case METRIC_CARDS.TOTAL_ORDERS:
          return totalOrders;
        case METRIC_CARDS.SYSTEM_HEALTH:
          return `${performanceMetrics.systemUptime}%`;
        case METRIC_CARDS.CONVERSION_RATE:
          return `${(performanceMetrics.conversionRate * 100).toFixed(1)}%`;
        case METRIC_CARDS.AVG_ORDER_VALUE:
          return `$${performanceMetrics.averageOrderValue.toFixed(2)}`;
        case METRIC_CARDS.CUSTOMER_SATISFACTION:
          return `${performanceMetrics.customerSatisfaction}/5.0`;
        case METRIC_CARDS.RECENT_TRANSACTIONS:
          return recentTransactions.length;
        default:
          return 0;
      }
    },
    [
      totalMerchants,
      activeDevices,
      supportTickets,
      totalRevenue,
      totalOrders,
      performanceMetrics,
      recentTransactions.length,
    ]
  );

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <HeaderLeft>
              <MainTitle level={1}>Admin Reports</MainTitle>
              <Subtitle>Comprehensive overview of platform performance and metrics</Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <RefreshButton
                icon={<FontAwesomeIcon icon={faSync} />}
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh
              </RefreshButton>
              <ExportButton
                icon={<FontAwesomeIcon icon={faFilePdf} />}
                onClick={() => handleExportReport(EXPORT_TYPES.COMPREHENSIVE_REPORT)}
                loading={isExporting}
              >
                Export All as PDF
              </ExportButton>
              {isExporting && (
                <StyledProgress percent={exportProgress} size="small" />
              )}
            </HeaderRight>
          </HeaderSection>

          {error && (
            <StyledAlert
              message="Error Loading Reports"
              description={error}
              type="error"
              showIcon
              action={
                <ActionButton size="small" onClick={handleRefresh}>
                  Retry
                </ActionButton>
              }
            />
          )}

          <MetricsGrid gutter={[8, 8]}>
            {loading && !lastUpdated ? (
              // Show skeleton loading for all metric cards on initial load
              Array.from({ length: METRIC_CARDS_CONFIG.length }).map((_, index) => (
                <Col key={`skeleton-${index}`} xs={24} sm={12} md={8} lg={6} xl={4} xxl={4}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Col>
              ))
            ) : (
              METRIC_CARDS_CONFIG.map((config) => (
                <Col key={config.id} xs={24} sm={12} md={8} lg={6} xl={4} xxl={4}>
                  {config.id === METRIC_CARDS.TOTAL_MERCHANTS ||
                  config.id === METRIC_CARDS.ACTIVE_DEVICES ||
                  config.id === METRIC_CARDS.SUPPORT_TICKETS ? (
                    <MetricCard
                      {...config}
                      value={getMetricValue(config.id)}
                      onClick={config.onClick ? () => handleMetricClick(config.id) : undefined}
                      isSelected={selectedMetric === config.id}
                    />
                  ) : (
                    <MetricCard
                      {...config}
                      value={getMetricValue(config.id)}
                      onClick={undefined}
                      isSelected={false}
                    />
                  )}
                </Col>
              ))
            )}
          </MetricsGrid>

          <SummaryCard>
            <MainTitle level={4}>
              Data Summary & Export Options
            </MainTitle>
            {loading && !lastUpdated ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <>
                <SummaryGrid gutter={[16, 12]}>
                  <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                    <SummaryBox>
                      <SummaryValue color="#3b82f6">{merchants.length}</SummaryValue>
                      <SummaryLabel>Total Merchants</SummaryLabel>
                      <ActionButton
                        size="small"
                        type="primary"
                        icon={<FontAwesomeIcon icon={faFilePdf} />}
                        onClick={() => handleExportReport(EXPORT_TYPES.MERCHANTS_OVERVIEW)}
                      >
                        Export PDF
                      </ActionButton>
                    </SummaryBox>
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                    <SummaryBox>
                      <SummaryValue color="#10b981">{devices.length}</SummaryValue>
                      <SummaryLabel>Active Devices</SummaryLabel>
                      <ActionButton
                        size="small"
                        type="primary"
                        icon={<FontAwesomeIcon icon={faFilePdf} />}
                        onClick={() => handleExportReport(EXPORT_TYPES.ACTIVE_DEVICES)}
                      >
                        Export PDF
                      </ActionButton>
                    </SummaryBox>
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                    <SummaryBox>
                      <SummaryValue color="#f59e0b">{recentTransactions.length}</SummaryValue>
                      <SummaryLabel>Recent Transactions</SummaryLabel>
                    </SummaryBox>
                  </Col>
                </SummaryGrid>
                <DividerStyled />
                <LastUpdatedText>
                  Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Never'} |
                  Auto-refresh every 5 minutes
                </LastUpdatedText>
              </>
            )}
          </SummaryCard>

          <MerchantsSection
            isOpen={selectedMetric === METRIC_CARDS.TOTAL_MERCHANTS}
            onExport={() => handleExportReport(EXPORT_TYPES.MERCHANTS_OVERVIEW)}
          />
          <DevicesSection
            isOpen={selectedMetric === METRIC_CARDS.ACTIVE_DEVICES}
            onExport={() => handleExportReport(EXPORT_TYPES.ACTIVE_DEVICES)}
          />
          <SupportTicketsSection isOpen={selectedMetric === METRIC_CARDS.SUPPORT_TICKETS} />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(AdminReports);
